/**
 * Copy of Operations › Auto ordering (`components/Operations/`), from
 * VentaHub's `pages/AutoOrdering.tsx`. Its column help is kept nearly
 * verbatim; two hints are corrected where VentaHub's text disagreed with its
 * own engine — Sales (Vendor) is VO invoices, not Vendor Central POs, and an
 * event lifts every SKU, not one marketplace's.
 */
export const autoOrdering = {
  title: 'Auto ordering',
  subtitle: 'Per-pool stock-cover forecast and reorder suggestions.',
  intro:
    'Pick a warehouse, set how far ahead to forecast and your real shipping days, then run the forecast for per-SKU stock cover and reorder suggestions. Order days is the cover the order buys: type it to set Forecast days. Upload data tries event calendars, price changes or marketing plans — they apply until you refresh and are never saved.',
  filters: 'Forecast settings',
  warehouse: 'Warehouse',
  pickWarehouse: 'Pick a warehouse',
  brands: 'Brands',
  allBrands: 'All brands',
  forecastDays: 'Forecast days',
  forecastHint: 'Days ahead to look',
  shippingDays: 'Shipping days',
  shippingHint: 'Supplier transit time',
  bufferDays: 'Buffer days',
  bufferHint: 'Safety stock',
  orderDays: 'Order days',
  orderHint: 'Cover the order buys',
  showTimeline: 'See these days drawn on a line',
  run: 'Run forecast',
  running: 'Running…',
  invalid: {
    whole: '{{name}} must be a whole number.',
    forecast: 'Forecast days must be at least 1.',
    shipping: 'Shipping days must be at least 1.',
    buffer: 'Buffer days cannot be below 0.',
    shippingOverForecast: 'Shipping days cannot be more than forecast days.',
  },
  upload: 'Upload data',
  uploaded_one: 'Upload data · {{count}} row',
  uploaded_other: 'Upload data · {{count}} rows',
  formulas: 'View formulas',
  export: 'Export to Excel',
  prompt: {
    title: 'Run the forecast',
    hint: 'Pick a warehouse and the days, then Run forecast to see each SKU’s stock cover and what to reorder.',
  },
  empty: {
    noneTitle: 'Nothing to forecast',
    noneHint: 'This warehouse and these brands have no sales, stock or stock on its way.',
    filteredTitle: 'No SKU matches',
    filteredHint: 'Clear the search or the status to see every SKU.',
  },
  stats: {
    reorder: 'Reorder',
    reorderHint: '{{units}} units to order',
    loss: 'Loss SKUs',
    lossHint: 'Losing money: not a purchase',
    covered: 'Covered',
    coveredHint: 'Stock covers the window',
    skus: 'SKUs forecast',
    skusHint: 'Run {{date}} · FD {{fd}} · SD {{sd}} · BD {{bd}}',
    showOnly: 'Show only {{status}}',
    showAll: 'Show every SKU',
  },
  card: '{{count}} SKUs',
  search: 'Search by SKU code, name or ASIN…',
  total: 'Total',
  noProfit: 'PPP, CP and Profitability need the finance:view permission.',
  status: { loss: 'Loss SKU', yes: 'Yes', no: 'No' },
  covered: 'Covered',
  openBreakdown: 'Open the forecast for {{sku}}',
  moreAsins_one: '{{count}} more listing',
  moreAsins_other: '{{count}} more listings',
  groups: {
    product: 'Product',
    sales: 'Sales · last 90 days',
    rate: 'Rate and levers',
    demand: 'Demand',
    stock: 'Stock now',
    order: 'Order',
    profit: 'Profit · last 90 days',
  },
  columns: {
    sku: 'SKU',
    asin: 'ASIN',
    name: 'Name',
    brand: 'Brand',
    category: 'Category',
    subcategory: 'Sub Cat',
    salesVendor: 'Sales (Vendor)',
    salesSeller: 'Sales (Seller)',
    salesDf: 'Sales (DF)',
    salesOffline: 'Sales (Offline)',
    salesTotal: 'Total sales',
    saleDays: 'Total seller / DF sale days',
    drrOrganic: 'Organic DRR',
    drrAms: 'AMS DRR',
    event: 'Event(d) Multiplier',
    ad: 'Ad(d) Multiplier',
    price: 'Price(d) Multiplier',
    demandCover: 'Overall demand (FD + BD)',
    demandLead: 'Shipping time demand (SD)',
    onHandMarketplace: 'On hand (FBA)',
    onHandWarehouse: 'On hand (Own WH)',
    onHand: 'On hand',
    inTransit: 'In transit',
    orderQty: 'Order qty',
    ppp: 'PPP (90d)',
    cp: 'CP (90d)',
    profitability: 'Profitability(90d)',
    reorder: 'Reorder status',
  },
  hints: {
    asin: 'Amazon ASIN(s) for this SKU, across every store this warehouse supplies. Click to open the listing on that store’s own Amazon site. When the SKU has more than one listing, the best seller over the last 90 days is shown and “+N” lists the rest. The same ASIN on two stores of one Amazon site is shown once. Blank means the SKU has no Amazon listing.',
    category:
      'The SKU master’s own merchandising category (Product Data), e.g. Cricket, Apparels, Badminton. Not Amazon’s browse-node category. Blank means the SKU master has none.',
    subcategory:
      'The second level of the same taxonomy, e.g. English Willow Bat, T-Shirt, Shoes. It is NOT nested under Category — “Shoes” appears under Cricket, Football and Badminton alike, so filter on both columns together if you need one sport’s footwear.',
    salesVendor:
      'Units invoiced to the marketplace as a vendor (VO invoices) over the last 90 days — a raw total, not a daily rate. Own-warehouse demand only. Divided by a flat 90 together with offline units.',
    salesSeller:
      'Units sold through this warehouse’s stores on the marketplaces over the last 90 days — a raw total, not a daily rate. Orders that shipped, including ones later returned.',
    salesDf:
      'Units invoiced on direct fulfilment / dropship over the last 90 days — a raw total, not a daily rate. Own-warehouse demand only. Divided by Total seller / DF sale days together with seller units.',
    salesOffline:
      'Units invoiced to offline retailers over the last 90 days — a raw total, not a daily rate. Own-warehouse demand only. Divided by a flat 90 together with vendor units.',
    salesTotal:
      'Every channel added together — Vendor + Seller + DF + Offline — over the last 90 days. The two halves of Organic DRR use different divisors: seller + DF units divide by Total seller / DF sale days, vendor and offline units by the full 90.',
    saleDays:
      'The divisor for the seller + DF half of Organic DRR. Start = the later of (today − 90) and the EARLIER of the last stock receipt here and the first seller/DF sale; end = the last seller/DF sale (never after today). Both days count. Vendor and offline units do not use this divisor; they divide by a flat 90.',
    drrOrganic:
      'Organic Sales DRR — the rate the forecast runs on. (Seller + DF units) ÷ Total seller / DF sale days, plus (vendor + offline units) ÷ 90. Two divisors: a vendor invoice is one batch on one date, so dividing it by a short span would read it as a daily rate. Both fulfilment pools are summed; open the row for the per-pool split. Blank when the sale days are under 5 — too short a span to divide by honestly; the row sizes its order off sales instead.',
    drrAms:
      'AMS Sales DRR — ad-attributed units per day over the last 90 days, from the Amazon Ads report. Reported only: it does NOT feed the forecast. The Ad(d) Multiplier scales Organic DRR, as the Event and Price multipliers do.',
    event:
      'Event(d) — the largest uplift from the uploaded event calendar anywhere in the forecast days (1.00× = no event). It lifts every SKU for the days it covers, and adds with the other levers: a 2.0× event doubles sales on those days.',
    ad: 'Ad(d) — the budget change scaled by how much of this SKU your ads drive: 1 + (new budget ÷ old budget − 1) × AMS share, for days inside an uploaded marketing plan. A 1.5× budget at a 40% share shows 1.20×. 1.00× everywhere else: it only moves when you upload a window.',
    price:
      'Price(d) — the most extreme planned-price effect in the forecast days: 1 ÷ (1 − discount), so a 20% discount reads 1.25×. It assumes revenue holds — cut the price and sell proportionally more. 1.00× means no price change is planned.',
    demandCover:
      'Demand(FD + BD) — everything this SKU is forecast to sell across the Forecast days plus the Buffer days: the whole window a new order has to see you through. Organic DRR × [(FD + BD) + each lever’s days × (its multiplier − 1)].',
    demandLead:
      'Demand(SD) — what sells off the shelf you already have during the Shipping days, before an order placed today could land. Subtracted twice in the order formula: once because the order cannot cover sales that already happened, and once because those sales eat into your current stock.',
    onHandMarketplace:
      'Marketplace-held stock (FBA, FBN) at this warehouse’s stores, from the latest inventory report. Seller Flex stock is ours, so it is counted under Own WH instead.',
    onHandWarehouse:
      'Own-warehouse stock available now: inward and allotted box units, less blocked units.',
    onHand:
      'Total current stock across both fulfilment pools — the FBA and Own WH columns added together.',
    inTransit:
      'Units ordered from suppliers for this warehouse that have not arrived yet. Transfers between our own companies are left out.',
    orderQty:
      'Units to order now. Demand(FD + BD) − Demand(SD) − max(0, on hand + in transit − Demand(SD)), applied once to this row’s own columns: the demand from the day a new order lands to the end of the forecast, less whatever stock is still on the shelf by then. EXCEPTION: when the SKU has seller or DF sales but under 5 sale days, it reads max(0, (Seller + DF sales) × 5 + (vendor + offline sales) ÷ 90 × Order days − on hand − in transit).',
    ppp: 'PPP — profit before ads over the last 90 days, in the warehouse’s currency: sales − cost of goods − marketplace fees, across every store at this warehouse where the SKU sold, plus the warehouse’s invoices. Blank when the SKU had no sales with a known profit.',
    cp: 'CP — PPP less ad spend: what the SKU actually earned. A SKU positive in PPP and negative here is an advertising decision, not a buying one.',
    profitability:
      'CP ÷ cost of goods sold × 100 over the last 90 days — a markup over cost, as Loss Making SKUs measures it. Blank, never 0%, unless profit and cost are both known.',
    reorder:
      'LOSS SKU — Profitability(90d) is negative, so the answer is not a purchase; this wins even when the SKU is short. YES — the SKU makes money and Order qty is above 0. NO — current stock covers the window. Open the row for the per-pool split, which can show one pool short while the SKU as a whole is covered — that is a transfer, not a purchase.',
  },
  orderStatus: 'Order status',
  orderStatusHint:
    'Ticked rows go on the vendor order. Short SKUs that make money start ticked; loss-makers and covered SKUs start unticked.',
  tickAll: 'Tick every SKU shown for the vendor order',
  tickRow: 'Put {{sku}} on the vendor order',
  createOrder: 'Create vendor order',
  createOrderCount_one: 'Create vendor order · {{count}} SKU',
  createOrderCount_other: 'Create vendor order · {{count}} SKUs',
  tickFirst: 'Run the forecast, then tick at least one SKU in Order status.',
  order: {
    title: 'Review vendor order',
    intro:
      'Every SKU ticked in the grid is pre-selected. Tick or untick to choose what goes on the order, change any Order qty to a whole number between 0 and 1.2× its forecast, then pick a vendor that can supply all of them.',
    noCostWarning_one:
      '1 SKU has no vendor cost on file at this warehouse and was deselected. Upload vendor costs for it to include it in an order.',
    noCostWarning_other:
      '{{count}} SKUs have no vendor cost on file at this warehouse and were deselected. Upload vendor costs for them to include them in an order.',
    noneTicked: 'No SKUs are ticked in the grid.',
    selectAll: 'Select every SKU',
    selectSku: 'Put {{sku}} on the order',
    sku: 'SKU',
    name: 'Name',
    forecast: 'Forecast',
    qty: 'Order qty',
    qtyRange: '0–1.2× forecast',
    qtyFor: 'Order qty for {{sku}}',
    cost: 'Cost',
    currency: 'Currency',
    max: 'max {{max}}',
    reset: 'Reset to forecast',
    noCost: 'No cost',
    problems: {
      empty: 'Enter a quantity',
      number: 'Enter a number',
      whole: 'Whole units only',
      min: 'Minimum is 0',
      max: 'Maximum is {{max}} (1.2× forecast)',
    },
    vendor: 'Vendor',
    pickVendor: 'Pick a vendor',
    noVendor:
      'No single vendor quotes every selected SKU in one currency at this warehouse. Untick some SKUs so one vendor can cover the rest, or upload vendor costs for the gaps first.',
    summary_one: '{{count}} SKU · {{units}} units',
    summary_other: '{{count}} SKUs · {{units}} units',
    proceed: 'Proceed',
    drafting: 'Drafting…',
    draftTitle: 'New vendor order',
    draftIntro:
      'The order as the vendor order form will open with it: check it, then create it there.',
    orderDate: 'Order date',
    validTill: 'Valid till',
    back: 'Back to review',
    download: 'Download order',
    create: 'Create vendor order',
    notLinked:
      'Saving happens in Operations › Vendor Orders, which this button opens once that page is linked. Until then, download the order to enter it there.',
    notLinkedShort: 'Opens Operations › Vendor Orders once that page is linked.',
  },
  breakdown: {
    title: '{{sku}} · forecast',
    intro:
      'How this row’s order is worked out, step by step, from its own columns. Day 0 is {{anchor}}; sales are the 90 days before it.',
    formula: 'Formula',
    values: 'This SKU’s values',
    result: 'Result',
    steps: {
      drr: '1. Organic DRR',
      drrFormula: '(seller + DF) ÷ sale days + (vendor + offline) ÷ 90',
      cover: '2. Demand(FD + BD)',
      lead: '3. Demand(SD)',
      demandFormula:
        'Organic DRR × [ days + Price extra + Event extra + AMS extra ], each extra = Σ (multiplier − 1) over the window',
      surviving: '4. Stock left when the order lands',
      survivingFormula: 'max(0, on hand + in transit − Demand(SD))',
      order: '5. Order qty',
      orderFormula: 'Demand(FD + BD) − Demand(SD) − stock left, rounded half up, never below 0',
      reorder: '6. Reorder',
      reorderFormula: 'Order qty > 0, and the SKU is not losing money',
    },
    noRate: 'No sales in the last 90 days: no demand to forecast.',
    thin: {
      title: 'Too few sale days for a rate',
      body: 'This SKU sold on fewer than 5 seller / DF days, too short a span to divide by honestly. Its order is sized off what sold instead:',
      formula:
        'max(0, (seller + DF sales) × 5 + (vendor + offline sales) ÷ 90 × order days − on hand − in transit)',
    },
    pools: 'Where the stock actually sits',
    poolsNote:
      'Each pool on its own. Pool orders do not add up to the row’s Order qty, which is worked once over both pools — a short pool beside a covered one is a transfer, not a purchase.',
    pool: { marketplace: 'Marketplace (FBA)', warehouse: 'Own warehouse' },
    poolColumns: {
      pool: 'Pool',
      onHand: 'On hand',
      inTransit: 'In transit',
      drr: 'Organic DRR',
      cover: 'Demand(FD+BD)',
      lead: 'Demand(SD)',
      coverDays: 'Cover (days)',
      order: 'Pool order qty',
    },
    yes: 'Reorder',
    no: 'No reorder',
    loss: 'Loss SKU — not a purchase',
  },
  timeline: {
    title: 'The days your order covers',
    shipping: 'Shipping {{days}}d',
    buffer: 'Buffer {{days}}d',
    order: 'Order days {{days}}d',
    forecast: 'Forecast {{days}}d',
    day: 'Day {{day}}',
    lever: '{{name}}',
    endsEarly: 'ends before the order lands — it only lowers stock you already have',
    warnSame: 'Shipping days equal Forecast days: the order only ever covers the Buffer days.',
    formula: 'Order qty = Demand(FD + BD) − Demand(SD) − max(0, stock + in transit − Demand(SD))',
  },
  formulasDialog: {
    title: 'Auto ordering forecast formulas',
    subtitle: 'How every number in the grid is worked out, from 90 days of sales to the order.',
    flowLabel: 'The forecast, step by step',
    flow: {
      drr: 'Organic DRR',
      multipliers: 'Multipliers',
      demand: 'Demand',
      order: 'Order qty',
      example: 'Worked example',
    },
    legend: {
      fd: 'Forecast days',
      sd: 'Shipping days',
      bd: 'Buffer days',
    },
    vars: {
      sellerDf: 'seller + DF units',
      saleDays: 'seller / DF sale days',
      vendorOffline: 'vendor + offline units',
      ninety: '90',
      drr: 'Organic DRR',
      days: 'X',
      priceDays: 'Price days',
      eventDays: 'Event days',
      amsDays: 'AMS days',
      price: 'Price',
      event: 'Event',
      ams: 'AMS',
      cover: 'Demand(FD + BD)',
      lead: 'Demand(SD)',
      stock: 'on hand + in transit',
      discount: 'discount',
      newBudget: 'new budget',
      oldBudget: 'old budget',
      share: 'AMS share',
      uplift: 'uplift',
      demandX: 'Demand(X)',
      orderQty: 'Order qty',
    },
    drr: {
      title: 'Organic DRR',
      lead: 'The daily rate the whole forecast runs on.',
      days: 'Sale days run from the later of today − 90 and the earlier of the last stock receipt and the first sale, to the last sale — both days counted.',
      window:
        'Vendor and offline units divide by the full 90: a vendor invoice is one batch on one date, and a short span would read it as a daily rate.',
    },
    multipliers: {
      title: 'Multipliers',
      lead: 'Each lever is a multiplier on a day, 1.0 when nothing is planned. They come from Upload data.',
      event: {
        name: 'Event',
        note: 'Used exactly as uploaded.',
        example: '1.2 → 20% more than a normal day',
      },
      price: {
        name: 'Price',
        note: 'Discount is the cut, 1–99%.',
        example: '20% off → 1 ÷ 0.80 = 1.25×',
      },
      ams: {
        name: 'AMS',
        note: 'Budgets are window totals; share is how much demand the ads drive.',
        example: '1500 vs 1000 at 100% → 1.5×',
      },
    },
    demand: {
      title: 'Demand over a window',
      lead: 'X is FD + BD for the whole cover, or SD for the shipping wait. Lever days are the days of that window each lever covers.',
      add: 'Levers add, they never compound',
      addExample: '1.25 and 1.2 on the same day add 0.25 + 0.2 = 0.45 extra — not 1.25 × 1.2.',
    },
    order: {
      title: 'Order qty',
      lead: 'What sells from the day the order lands to the end of the window, less what is still on the shelf by then.',
      terms: {
        cover: 'everything to cover',
        lead: 'sells before it lands',
        left: 'stock still on the shelf',
      },
      once: 'Worked once over the SKU’s totals, not per pool and added, so a row reconciles with its own columns. Rounded half up; never below 0.',
      thin: 'Under 5 sale days there is no honest rate: the order is (seller + DF) × 5 + (vendor + offline) ÷ 90 × order days, less stock.',
    },
    example: {
      title: 'Worked example',
      setup: 'FD 25 · BD 5 · SD 10',
      levers:
        'Price 1.25× on 10 days (6 in SD) · Event 1.2× on 7 days (6 in SD) · AMS 1.5× at 100% on 15 days (10 in SD)',
      columns: {
        sku: 'SKU',
        drr: 'Organic DRR',
        cover: 'Demand(FD + BD)',
        lead: 'Demand(SD)',
        left: 'Stock left',
        order: 'Order qty',
      },
      sku: 'SKU {{n}}',
      note: 'The three SKUs of VentaHub’s sheet, reproduced by this engine to the unit.',
    },
  },
  uploadDialog: {
    title: 'Upload data',
    temporary:
      'Uploads are temporary: they apply to your forecast runs on this page and are cleared when you refresh. Nothing is saved, and other users do not see them.',
    kindsLabel: 'What to upload',
    loadedBadge_one: '{{count}} row in use',
    loadedBadge_other: '{{count}} rows in use',
    notLoaded: 'Not uploaded',
    steps: {
      template: 'Get the template',
      templateHint: 'One header row, then a row per window. Hover a column for what goes in it.',
      file: 'Add your file',
      fileHint: '.xlsx or .csv, up to 5 MB.',
      check: 'Check and apply',
      checkHint: 'The file is read in full before anything changes: one bad row and nothing loads.',
    },
    template: 'Download template',
    column: 'Column',
    required: 'Required',
    optional: 'Optional',
    meaning: 'What goes in it',
    drop: 'Drop the file here, or',
    browse: 'browse',
    dropActive: 'Drop to add it',
    wrongType: 'Only .xlsx and .csv files can be read.',
    replaceFile: 'Remove the file',
    size: '{{size}} KB',
    check: 'Check file',
    checking: 'Reading the file…',
    readOk_one: '{{count}} row read, all good.',
    readOk_other: '{{count}} rows read, all good.',
    ended_one: '{{count}} has already ended and is left out.',
    ended_other: '{{count}} have already ended and are left out.',
    allEnded: 'Every window in this file has already ended, so none of it applies.',
    preview: 'Preview',
    previewMore_one: 'and {{count}} more row',
    previewMore_other: 'and {{count}} more rows',
    replaces_one: 'Replaces the {{count}} row in use now.',
    replaces_other: 'Replaces the {{count}} rows in use now.',
    apply_one: 'Apply {{count}} row',
    apply_other: 'Apply {{count}} rows',
    applied_one: '{{kind}}: {{count}} row now applies to the forecast.',
    applied_other: '{{kind}}: {{count}} rows now apply to the forecast.',
    rejected_one:
      '{{count}} row has a problem, so nothing was loaded. Fix it and check the file again.',
    rejected_other:
      '{{count}} rows have a problem, so nothing was loaded. Fix them and check the file again.',
    rejectedMore_one: '…and {{count}} more problem row.',
    rejectedMore_other: '…and {{count}} more problem rows.',
    errorRow: 'Row',
    errorSku: 'SKU',
    errorProblem: 'Problem',
    inUse: 'In use now',
    noneInUse: 'Nothing uploaded for this yet.',
    clear: 'Clear all',
    clearTitle: 'Clear the {{kind}}?',
    clearBody: 'Its rows stop applying to the forecast. Nothing saved is touched.',
    removeTitle: 'Remove this row?',
    remove: 'Remove {{what}}',
    clearConfirm: 'Clear',
    removeConfirm: 'Remove',
    kinds: {
      'event-calendar': {
        label: 'Event calendar',
        description: 'Days every SKU sells more than usual — Prime Day, a sale season.',
        formula: 'Event(d) = uplift on every day in the window',
        columns: {
          event: 'The event’s name.',
          marketplace: 'Where it runs, for your reference.',
          region: 'Optional, for your reference.',
          window_start: 'First day, DD-MM-YYYY.',
          window_end: 'Last day, DD-MM-YYYY.',
          uplift: 'A multiplier above 0: 1.8 is 80% more than a normal day.',
        },
      },
      'price-changes': {
        label: 'Price changes',
        description: 'A planned price cut on a SKU.',
        formula: 'Price Multiplier = 1 ÷ (1 − discount)',
        columns: {
          sku: 'The SKU code, as in Product Data.',
          discount: 'The cut, a percent from 1 to 99: 20, 20% or a cell formatted 20%.',
          window_start: 'First day, DD-MM-YYYY.',
          window_end: 'Last day, DD-MM-YYYY.',
        },
      },
      'marketing-plan': {
        label: 'Marketing plan',
        description: 'A change to a SKU’s ad budget.',
        formula: 'AMS Multiplier = 1 + (new budget ÷ old budget − 1) × AMS share',
        columns: {
          sku: 'The SKU code, as in Product Data.',
          new_budget: 'The window’s new budget, 0 or more.',
          old_budget: 'The window’s budget before, above 0.',
          ams_share: 'How much of the SKU’s demand its ads drive, a percent from 0 to 100.',
          window_start: 'First day, DD-MM-YYYY.',
          window_end: 'Last day, DD-MM-YYYY.',
        },
      },
    },
    table: {
      event: 'Event',
      where: 'Where',
      sku: 'SKU',
      window: 'Window',
      uplift: 'Uplift',
      discount: 'Discount',
      budget: 'New / old budget',
      share: 'AMS share',
    },
  },
} as const
